import { useState, useMemo, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Sparkles, CalendarClock } from "lucide-react";
import { useCompanyId } from "@/lib/CompanyContext";
import { CalendarTaskReviewModal } from "@/components/CalendarTaskReviewModal";
import {
  issuesApi,
  type Issue,
  type IssueDocument,
  type IssueAttachment,
} from "@/api/issues";
import { templatesApi, type ArtBriefing } from "@/api/templates";
import ArtBriefingQuestions from "@/components/ArtBriefingQuestions";
import { productLibraryApi } from "@/api/product-library";
import { ReferenceImagePickerModal } from "@/components/ReferenceImagePickerModal";
import { FeedbackWidget } from "@/components/FeedbackWidget";
import { getTemplateMeta } from "@/lib/templateMeta";
import { useIsMobile } from "@/lib/useIsMobile";
import { Icon } from "@/components/Icon";
import { ImageLightbox } from "@/components/ImageLightbox";
import { MdInline } from "@/components/MarkdownLite";
import {
  PageHeader,
  StatusPill,
  DocumentSection,
  MetricGrid,
  NumberedList,
  RelatedTaskRow,
  InfoField,
  BlockedBanner,
  type Metric,
} from "@/components/ui";

function formatBR(dateStr: string | null | undefined) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

// =============================================================================
// MarkdownLite — renderização leve usada como fallback. Spans inline vêm de
// MdInline (`@/components/MarkdownLite`) — React elements, sem HTML cru, sem
// dangerouslySetInnerHTML (protege contra XSS persistente vindo de texto da
// IA / fontes externas como Apify/Perplexity scrapes).
// =============================================================================

function isTableSeparator(line: string): boolean {
  // |---|---|---|  ou |:---|:---:|---:|
  return /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/.test(line);
}

function parseTableRow(line: string): string[] {
  const trimmed = line.trim().replace(/^\||\|$/g, "");
  return trimmed.split("|").map((c) => c.trim());
}

function MarkdownLite({ body }: { body: string }) {
  const blocks = useMemo(() => {
    const lines = body.split("\n");
    const out: React.ReactNode[] = [];
    let listBuffer: string[] = [];
    let isOrdered = false;

    function flushList(idx: number) {
      if (listBuffer.length === 0) return;
      const Tag = isOrdered ? "ol" : "ul";
      out.push(
        <Tag key={`list-${idx}`} style={{ paddingLeft: 20, marginBottom: 12, marginTop: 0 }}>
          {listBuffer.map((item, i) => (
            <li key={i} style={{ marginBottom: 4, lineHeight: 1.55 }}>
              <MdInline text={item} />
            </li>
          ))}
        </Tag>,
      );
      listBuffer = [];
    }

    let i = 0;
    while (i < lines.length) {
      const line = lines[i] ?? "";
      // Tabela pipe: linha 1 com |, linha 2 separator
      if (
        line.includes("|") &&
        i + 1 < lines.length &&
        isTableSeparator(lines[i + 1] ?? "")
      ) {
        flushList(i);
        const headers = parseTableRow(line);
        const rows: string[][] = [];
        let j = i + 2;
        while (j < lines.length && (lines[j] ?? "").includes("|") && (lines[j] ?? "").trim() !== "") {
          rows.push(parseTableRow(lines[j] ?? ""));
          j++;
        }
        out.push(
          <div
            key={`table-${i}`}
            style={{
              overflow: "auto",
              marginBottom: 14,
              border: "1px solid var(--wf-ink-5)",
              borderRadius: 10,
              background: "var(--wf-paper)",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
                fontFamily: "var(--wf-font-body)",
              }}
            >
              <thead>
                <tr>
                  {headers.map((h, hi) => (
                    <th
                      key={hi}
                      style={{
                        textAlign: "left",
                        padding: "8px 12px",
                        borderBottom: "1px solid var(--wf-ink-5)",
                        fontWeight: 600,
                        color: "var(--wf-ink-2)",
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        background: "#fafaf6",
                      }}
                    >
                      <MdInline text={h} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cell, ci) => (
                      <td
                        key={ci}
                        style={{
                          padding: "8px 12px",
                          borderBottom:
                            ri < rows.length - 1
                              ? "1px solid var(--wf-ink-5)"
                              : "none",
                          color: "var(--wf-ink)",
                          verticalAlign: "top",
                        }}
                      >
                        <MdInline text={cell} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>,
        );
        i = j;
        continue;
      }

      if (line.startsWith("### ")) {
        flushList(i);
        out.push(
          <h3
            key={i}
            className="wf-display"
            style={{
              fontSize: 20,
              marginTop: 16,
              marginBottom: 6,
              fontWeight: 700,
              color: "var(--wf-ink)",
              letterSpacing: "-0.01em",
            }}
          >
            {line.slice(4)}
          </h3>,
        );
      } else if (line.startsWith("- ") || line.startsWith("* ")) {
        if (isOrdered) flushList(i);
        isOrdered = false;
        listBuffer.push(line.slice(2));
      } else if (/^\d+\.\s/.test(line)) {
        if (!isOrdered) flushList(i);
        isOrdered = true;
        listBuffer.push(line.replace(/^\d+\.\s/, ""));
      } else if (line.trim() === "---") {
        flushList(i);
        out.push(
          <hr key={i} style={{ border: "none", borderTop: "1px dashed var(--wf-ink-4)", margin: "16px 0" }} />,
        );
      } else if (line.trim() === "") {
        flushList(i);
      } else {
        flushList(i);
        out.push(
          <p
            key={i}
            style={{ marginBottom: 10, marginTop: 0, lineHeight: 1.65 }}
          >
            <MdInline text={line} />
          </p>,
        );
      }
      i++;
    }
    flushList(lines.length);
    return out;
  }, [body]);

  return <div style={{ fontSize: 15, color: "var(--wf-ink)" }}>{blocks}</div>;
}

// =============================================================================
// Parser de seções H2
// =============================================================================

type Section = {
  title: string; // H2 sem o "## "
  /** Linhas dentro da seção (sem o header) */
  body: string;
  /** Slug pra match: "resumo", "numeros", "recomendacoes", "plano-de-acao", etc */
  slug: string;
};

function ApprovalBar({ issue }: { issue: Issue }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [rejectCloseOpen, setRejectCloseOpen] = useState(false);
  const [rejectCloseReason, setRejectCloseReason] = useState("");
  const [variationModalOpen, setVariationModalOpen] = useState(false);
  const [selectedVariationLabels, setSelectedVariationLabels] = useState<string[]>([]);
  // Pós-aprovação: modal "quer próxima etapa?" — só aparece se template tem
  // nextTemplateKey e não tem variações múltiplas (variation flow já cobre o
  // caso de etapa intermediária com escolha).
  const [nextStepModalOpen, setNextStepModalOpen] = useState(false);
  // Picker de reference images — aparece quando próxima etapa é arte e o
  // produto vinculado tem brand library com pelo menos 1 asset. Pendente
  // = guarda variações já selecionadas pra serem passadas no approve depois
  // que user escolher refs.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingApproveInput, setPendingApproveInput] = useState<{
    selectedVariations?: Array<{ label: string; fullText: string }>;
  } | null>(null);

  const invalidateIssue = () => {
    queryClient.invalidateQueries({ queryKey: ["issues", issue.id] });
    queryClient.invalidateQueries({ queryKey: ["companies", issue.companyId, "issues"] });
    queryClient.invalidateQueries({
      queryKey: ["companies", issue.companyId, "dashboard"],
    });
  };

  // Carrega lista de templates pra extrair nextStepHint do template atual
  const templatesQuery = useQuery({
    queryKey: ["templates"],
    queryFn: () => templatesApi.list(),
    staleTime: 10 * 60_000,
  });
  const currentTemplate = useMemo(
    () =>
      issue.templateKey
        ? templatesQuery.data?.find((t) => t.key === issue.templateKey)
        : null,
    [issue.templateKey, templatesQuery.data],
  );
  const hasNextStep = !!currentTemplate?.nextTemplateKey;
  const nextIsArt = (currentTemplate?.nextTemplateKey ?? "").endsWith("_arte");

  // Carrega library do produto vinculado (se houver) — usado pra decidir
  // se mostra o Picker antes de aprovar copy que vira arte.
  const libraryQuery = useQuery({
    queryKey: ["product-library", issue.companyId, issue.productId],
    queryFn: () => productLibraryApi.get(issue.companyId, issue.productId!),
    enabled: !!issue.productId && hasNextStep && nextIsArt,
    staleTime: 60_000,
  });
  const library = libraryQuery.data ?? null;
  const hasLibraryAssets =
    !!library &&
    (library.peopleLibrary.length > 0 || library.creativeLibrary.length > 0);

  // Variações do documento — usado quando há nextStep pra mostrar modal de
  // seleção antes de criar a sub-task da próxima etapa.
  const variationsQuery = useQuery({
    queryKey: ["issues", issue.id, "variations"],
    queryFn: () => issuesApi.variations(issue.id),
    enabled: hasNextStep && issue.status === "in_review",
  });
  const detectedVariations = variationsQuery.data?.variations ?? [];
  const hasMultipleVariations = variationsQuery.data?.hasMultiple ?? false;

  const approve = useMutation({
    mutationFn: (input: {
      selectedVariations?: Array<{ label: string; fullText: string }>;
      createNextStep?: boolean;
      referenceAssetIds?: string[];
    }) =>
      issuesApi.approve(issue.id, {
        selectedVariations: input.selectedVariations,
        createNextStep: input.createNextStep,
        referenceAssetIds: input.referenceAssetIds,
      }),
    onSuccess: (response) => {
      invalidateIssue();
      setVariationModalOpen(false);
      setNextStepModalOpen(false);
      setPickerOpen(false);
      setPendingApproveInput(null);
      // Se o backend criou sub-task da próxima etapa, vai direto pra ela
      if (response?.chainedSubtaskId) {
        navigate(`/execucao/${response.chainedSubtaskId}`);
      } else {
        navigate("/quadro");
      }
    },
  });

  const onClickApprove = () => {
    if (hasNextStep && hasMultipleVariations) {
      // Variações múltiplas — modal de seleção (já decide arte na hora)
      setSelectedVariationLabels([detectedVariations[0]!.label]);
      setVariationModalOpen(true);
    } else if (hasNextStep && nextIsArt && hasLibraryAssets) {
      // Próxima etapa é arte + tem brand library → abre Picker
      setPendingApproveInput({});
      setPickerOpen(true);
    } else if (hasNextStep) {
      // Tem próxima etapa, sem variações ou library — modal "quer próxima etapa?"
      setNextStepModalOpen(true);
    } else {
      // Sem próxima etapa — aprovação direta
      approve.mutate({});
    }
  };

  const onConfirmVariations = () => {
    const selected = detectedVariations.filter((v) =>
      selectedVariationLabels.includes(v.label),
    );
    if (selected.length === 0) return;
    const variationsPayload = selected.map((v) => ({
      label: v.label,
      fullText: v.fullText,
    }));
    // Se próxima etapa é arte e há library, encadeia Picker antes do approve
    if (nextIsArt && hasLibraryAssets) {
      setPendingApproveInput({ selectedVariations: variationsPayload });
      setVariationModalOpen(false);
      setPickerOpen(true);
      return;
    }
    approve.mutate({ selectedVariations: variationsPayload });
  };

  const onConfirmPicker = (assetIds: string[]) => {
    approve.mutate({
      ...(pendingApproveInput ?? {}),
      referenceAssetIds: assetIds,
    });
  };

  const toggleVariation = (label: string) => {
    setSelectedVariationLabels((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label],
    );
  };

  const reject = useMutation({
    mutationFn: (text: string) => issuesApi.reject(issue.id, text),
    onSuccess: () => {
      invalidateIssue();
      setRejectOpen(false);
      setReason("");
      navigate("/quadro");
    },
  });

  // Reprovar e ENCERRAR — encerra como done marcado "Reprovado", sem regenerar.
  const rejectClose = useMutation({
    mutationFn: (text: string) => issuesApi.rejectClose(issue.id, text),
    onSuccess: () => {
      invalidateIssue();
      setRejectCloseOpen(false);
      setRejectCloseReason("");
      navigate("/quadro");
    },
  });

  // Promove o documento aprovado em base de conhecimento reusável.
  // Backend reformata via Haiku e salva como KB type=custom (fallback) ou
  // competitor (pra pesquisas de concorrente). User pode marcar a KB em
  // tasks futuras pra ter o contexto sem rerodar a pesquisa.
  const promoteToKb = useMutation({
    mutationFn: () =>
      issuesApi.promoteToKb(issue.id, {
        kbType:
          currentTemplate?.key === "pesquisa_concorrentes" ? "competitor" : "custom",
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: ["companies", issue.companyId, "knowledge"],
      });
      // Toast simples — futuramente conectar no sistema de notificações global
      alert(
        result.isNew
          ? `✓ Base de conhecimento "${result.kbName}" criada (${result.costCents} ND Credits). Já pode marcar ela em outras tarefas.`
          : `✓ Base "${result.kbName}" atualizada com a versão mais recente.`,
      );
    },
    onError: (err) => {
      alert(`Não consegui salvar como Base de Conhecimento: ${(err as Error).message}`);
    },
  });

  if (issue.status !== "in_review") return null;

  const isArt = !!issue.templateKey?.endsWith("_arte");
  const busy =
    approve.isPending || reject.isPending || rejectClose.isPending || promoteToKb.isPending;
  const fullBtn: React.CSSProperties = {
    width: "100%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
  };

  return (
    <div
      className="no-print"
      style={{
        padding: 14,
        borderBottom: "1px solid var(--wf-ink-5)",
        background: "rgba(254,112,0,0.04)",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 13,
          fontWeight: 700,
          color: "var(--wf-ink)",
        }}
      >
        <Icon name="check" size={14} color="var(--nd-orange)" />
        Pronto pra revisar
      </div>
      <p style={{ fontSize: 12, color: "var(--wf-ink-3)", lineHeight: 1.45, margin: 0 }}>
        {hasNextStep && currentTemplate?.nextStepHint
          ? currentTemplate.nextStepHint
          : "Revise o entregável e decida. Pedir ajuste volta pra fila com o que você descrever, e o agente aprende."}
      </p>

      <button
        type="button"
        onClick={onClickApprove}
        disabled={busy}
        className="wf-btn wf-btn--primary"
        style={{ ...fullBtn, marginTop: 2 }}
      >
        <Icon name="check" size={13} color="#fff" />
        {approve.isPending
          ? hasNextStep
            ? "aprovando + criando próxima…"
            : "aprovando…"
          : hasNextStep
            ? hasMultipleVariations
              ? "Aprovar e escolher versão"
              : "Aprovar e seguir"
            : "Aprovar"}
      </button>
      <button
        type="button"
        onClick={() => setRejectOpen((v) => !v)}
        disabled={busy}
        className="wf-btn wf-btn--ghost"
        style={fullBtn}
      >
        <Icon name="x" size={13} /> {rejectOpen ? "Cancelar ajuste" : "Ajustar"}
      </button>
      <button
        type="button"
        onClick={() => {
          setRejectCloseOpen((v) => !v);
          setRejectOpen(false);
        }}
        disabled={busy}
        className="wf-btn wf-btn--ghost"
        style={{ ...fullBtn, color: "#d92d4a" }}
      >
        <Icon name="x" size={13} color="#d92d4a" />{" "}
        {rejectCloseOpen ? "Cancelar" : "Reprovar e encerrar"}
      </button>
      {rejectCloseOpen && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 2 }}>
          <p style={{ fontSize: 12, color: "var(--wf-ink-3)", lineHeight: 1.45, margin: 0 }}>
            Encerra a tarefa como <strong>Reprovada</strong>, sem refazer. O motivo
            vira aprendizado pra automação não repetir o erro. Pra tentar de novo,
            crie uma nova tarefa.
          </p>
          <textarea
            value={rejectCloseReason}
            onChange={(e) => setRejectCloseReason(e.target.value)}
            placeholder="Por que está reprovando? Seja específico. Isso vira aprendizado pras próximas tarefas."
            rows={4}
            autoFocus
            style={{
              width: "100%",
              padding: 10,
              border: "1px solid var(--wf-ink-5)",
              borderRadius: 8,
              fontFamily: "var(--wf-font-body)",
              fontSize: 13,
              resize: "vertical",
              background: "var(--wf-paper)",
              lineHeight: 1.5,
            }}
          />
          <button
            type="button"
            className="wf-btn wf-btn--primary"
            style={{ ...fullBtn, background: "#d92d4a", borderColor: "#d92d4a" }}
            onClick={() => rejectClose.mutate(rejectCloseReason)}
            disabled={!rejectCloseReason.trim() || rejectClose.isPending}
          >
            {rejectClose.isPending ? "encerrando…" : "Reprovar e encerrar"}
          </button>
        </div>
      )}
      {/* "Salvar como Base de Conhecimento" só faz sentido pra entregáveis de
          texto. Arte tem o fluxo "salvar na biblioteca artística" na galeria. */}
      {!isArt && (
        <button
          type="button"
          onClick={() => promoteToKb.mutate()}
          disabled={busy}
          className="wf-btn wf-btn--ghost"
          style={fullBtn}
          title="Salva o documento como base de conhecimento reusável pra outras tarefas"
        >
          <Icon name="book" size={12} />
          {promoteToKb.isPending ? "salvando…" : "Salvar como Base de Conhecimento"}
        </button>
      )}

      {rejectOpen && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 2 }}>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="O que precisa ajustar? Seja específico — esse texto vai pro agente refazer e vira aprendizado pras próximas tarefas."
            rows={4}
            autoFocus
            style={{
              width: "100%",
              padding: 10,
              border: "1px solid var(--wf-ink-5)",
              borderRadius: 8,
              fontFamily: "var(--wf-font-body)",
              fontSize: 13,
              resize: "vertical",
              background: "var(--wf-paper)",
              lineHeight: 1.5,
            }}
          />
          <button
            type="button"
            className="wf-btn wf-btn--primary"
            style={fullBtn}
            onClick={() => reject.mutate(reason)}
            disabled={!reason.trim() || reject.isPending}
          >
            {reject.isPending ? "enviando…" : "Enviar ajuste"}
          </button>
        </div>
      )}

      {nextStepModalOpen && (
        <NextStepModal
          currentTemplate={currentTemplate}
          isPending={approve.isPending}
          onConfirmGenerate={() => approve.mutate({ createNextStep: true })}
          onSkipNextStep={() => approve.mutate({ createNextStep: false })}
          onCancel={() => setNextStepModalOpen(false)}
        />
      )}

      {variationModalOpen && (
        <VariationSelectionModal
          variations={detectedVariations}
          selectedLabels={selectedVariationLabels}
          onToggle={toggleVariation}
          onCancel={() => setVariationModalOpen(false)}
          onConfirm={onConfirmVariations}
          isPending={approve.isPending}
          nextStepLabel={currentTemplate?.nextTemplateKey ?? "próxima etapa"}
        />
      )}

      {pickerOpen && library && (
        <ReferenceImagePickerModal
          library={library}
          companyId={issue.companyId}
          isPending={approve.isPending}
          onCancel={() => {
            setPickerOpen(false);
            setPendingApproveInput(null);
          }}
          onConfirm={onConfirmPicker}
        />
      )}
    </div>
  );
}

/**
 * Modal pós-aprovação que pergunta se user quer gerar a próxima etapa.
 * Aparece quando template tem nextTemplateKey configurado mas NÃO tem
 * variações múltiplas (esse caso é coberto pelo VariationSelectionModal).
 *
 * 3 caminhos:
 *  - Sim, gerar agora       → cria sub-task, redireciona pra Execucao
 *  - Não, finalizar aqui    → marca task como done, fim. Sem token gasto
 *  - Decido depois          → fecha modal sem aprovar (volta pra Resultado)
 *
 * Resolve o caso do user que tem designer próprio e não quer arte gerada,
 * ou que quer revisar a copy mais antes de gerar arte.
 */
function NextStepModal({
  currentTemplate,
  isPending,
  onConfirmGenerate,
  onSkipNextStep,
  onCancel,
}: {
  currentTemplate: { nextTemplateKey: string | null; nextStepHint: string | null } | null | undefined;
  isPending: boolean;
  onConfirmGenerate: () => void;
  onSkipNextStep: () => void;
  onCancel: () => void;
}) {
  // Resolve título humano da próxima etapa (fallback pro key bruto)
  const templatesQuery = useQuery({
    queryKey: ["templates"],
    queryFn: () => templatesApi.list(),
    staleTime: 10 * 60_000,
  });
  const nextTemplate = useMemo(() => {
    const key = currentTemplate?.nextTemplateKey;
    if (!key) return null;
    return templatesQuery.data?.find((t) => t.key === key) ?? null;
  }, [currentTemplate?.nextTemplateKey, templatesQuery.data]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(13,21,38,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          width: "min(520px, 100%)",
          background: "var(--wf-paper)",
          borderRadius: 14,
          border: "1px solid var(--wf-ink-5)",
          padding: 24,
          fontFamily: "var(--wf-font-body)",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        {/* Check verde + título */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: "rgba(76,175,80,0.12)",
              color: "#4CAF50",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            ✓
          </div>
          <h2
            className="wf-display"
            style={{
              fontSize: 20,
              fontWeight: 700,
              letterSpacing: "-0.015em",
              margin: 0,
            }}
          >
            Aprovado. E agora?
          </h2>
        </div>

        <p style={{ fontSize: 13.5, color: "var(--wf-ink-2)", lineHeight: 1.5, margin: 0 }}>
          {currentTemplate?.nextStepHint ?? (
            <>
              A próxima etapa sugerida é{" "}
              <strong style={{ color: "var(--wf-ink)" }}>
                {nextTemplate?.title ?? "a próxima etapa"}
              </strong>
              . Você pode gerar agora, ou finalizar essa tarefa aqui.
            </>
          )}
        </p>

        {/* Botões — gera (orange filled) · finaliza (outline) · depois (ghost) */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
          <button
            type="button"
            onClick={onConfirmGenerate}
            disabled={isPending}
            style={{
              padding: "12px 16px",
              background: "var(--nd-orange)",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 14,
              cursor: isPending ? "wait" : "pointer",
              opacity: isPending ? 0.6 : 1,
              fontFamily: "inherit",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            {isPending
              ? "criando próxima etapa…"
              : `Gerar ${nextTemplate?.title ?? "próxima etapa"}`}
          </button>
          <button
            type="button"
            onClick={onSkipNextStep}
            disabled={isPending}
            style={{
              padding: "10px 16px",
              background: "transparent",
              color: "var(--wf-ink)",
              border: "1px solid var(--wf-ink-5)",
              borderRadius: 10,
              fontWeight: 600,
              fontSize: 13.5,
              cursor: isPending ? "wait" : "pointer",
              fontFamily: "inherit",
            }}
          >
            Finalizar aqui (não gera próxima etapa)
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            style={{
              padding: "8px",
              background: "transparent",
              color: "var(--wf-ink-3)",
              border: "none",
              borderRadius: 8,
              fontWeight: 500,
              fontSize: 12.5,
              cursor: isPending ? "wait" : "pointer",
              fontFamily: "inherit",
            }}
          >
            Decido depois
          </button>
        </div>

        <p style={{ fontSize: 11, color: "var(--wf-ink-3)", margin: 0, textAlign: "center" }}>
          Se finalizar, ainda pode gerar a próxima etapa depois pelo card.
        </p>
      </div>
    </div>
  );
}

function VariationSelectionModal({
  variations,
  selectedLabels,
  onToggle,
  onCancel,
  onConfirm,
  isPending,
  nextStepLabel,
}: {
  variations: Array<{ label: string; preview: string; fullText: string }>;
  selectedLabels: string[];
  onToggle: (label: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  isPending: boolean;
  nextStepLabel: string;
}) {
  const count = selectedLabels.length;
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(13,21,38,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          width: "min(640px, 100%)",
          maxHeight: "80vh",
          background: "var(--wf-paper)",
          borderRadius: 14,
          border: "1px solid var(--wf-ink-5)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          fontFamily: "var(--wf-font-body)",
        }}
      >
        <header
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid var(--wf-ink-5)",
          }}
        >
          <h2
            className="wf-display"
            style={{
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: "-0.01em",
              margin: 0,
              marginBottom: 4,
            }}
          >
            Qual versão usar pra próxima etapa?
          </h2>
          <p style={{ fontSize: 12, color: "var(--wf-ink-3)", lineHeight: 1.5, margin: 0 }}>
            O agente entregou {variations.length} variações. Marque uma ou várias —
            cada uma marcada vira uma sub-tarefa de {nextStepLabel}.
          </p>
        </header>

        <div style={{ overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          {variations.map((v) => {
            const checked = selectedLabels.includes(v.label);
            return (
              <button
                key={v.label}
                type="button"
                onClick={() => onToggle(v.label)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: 12,
                  background: checked ? "rgba(255,112,0,0.06)" : "var(--wf-bg)",
                  border: checked
                    ? "1px solid var(--nd-orange)"
                    : "1px solid var(--wf-ink-5)",
                  borderRadius: 10,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  color: "inherit",
                  transition: "border-color 120ms",
                }}
              >
                <span
                  style={{
                    width: 18,
                    height: 18,
                    flexShrink: 0,
                    borderRadius: 4,
                    border: checked
                      ? "1.5px solid var(--nd-orange)"
                      : "1.5px solid var(--wf-ink-4)",
                    background: checked ? "var(--nd-orange)" : "transparent",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    marginTop: 1,
                  }}
                >
                  {checked ? "✓" : ""}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "var(--wf-ink)",
                      marginBottom: 4,
                    }}
                  >
                    {v.label}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--wf-ink-2)",
                      lineHeight: 1.45,
                      whiteSpace: "pre-wrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {v.preview}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <footer
          style={{
            padding: "12px 20px",
            borderTop: "1px solid var(--wf-ink-5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          <span style={{ fontSize: 12, color: "var(--wf-ink-3)" }}>
            {count === 0
              ? "Selecione ao menos uma"
              : count === 1
                ? "1 versão escolhida"
                : `${count} versões → ${count} sub-tasks`}
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={onCancel}
              disabled={isPending}
              className="wf-btn wf-btn--ghost wf-btn--sm"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isPending || count === 0}
              className="wf-btn wf-btn--primary wf-btn--sm"
            >
              {isPending ? "criando…" : `Aprovar e gerar ${count > 1 ? `${count} artes` : "arte"}`}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function downloadAsMarkdown(title: string, body: string) {
  const safeTitle = title.replace(/[^\w\s-]/g, "").replace(/\s+/g, "-").slice(0, 80) || "entregavel";
  const blob = new Blob([`# ${title}\n\n${body}`], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = window.document.createElement("a");
  a.href = url;
  a.download = `${safeTitle}.md`;
  window.document.body.appendChild(a);
  a.click();
  window.document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function parseSections(body: string): Section[] {
  const lines = body.split("\n");
  const sections: Section[] = [];
  let currentTitle: string | null = null;
  let currentBody: string[] = [];

  for (const line of lines) {
    if (line.startsWith("## ") && !line.startsWith("### ")) {
      // Fecha seção anterior
      if (currentTitle !== null) {
        sections.push({
          title: currentTitle,
          body: currentBody.join("\n").trim(),
          slug: slugify(currentTitle),
        });
      }
      currentTitle = line.slice(3).trim();
      currentBody = [];
    } else if (currentTitle !== null) {
      currentBody.push(line);
    }
  }
  if (currentTitle !== null) {
    sections.push({
      title: currentTitle,
      body: currentBody.join("\n").trim(),
      slug: slugify(currentTitle),
    });
  }
  return sections;
}

// =============================================================================
// Cards especializados — V1 do protótipo
// =============================================================================

function ResumoCard({ section }: { section: Section }) {
  return (
    <DocumentSection title="Resumo em 30 segundos">
      <MarkdownLite body={section.body} />
    </DocumentSection>
  );
}

/**
 * Detecta linhas no formato métrica.
 * Aceita ambas as formas que os agentes geram:
 *   - **Label:** Valor             (colon dentro do bold — comum)
 *   - **Label:** Valor (delta)
 *   - **Label**: Valor             (colon fora do bold)
 *   - **Label**: Valor — diferença de 25x   (qualquer cauda como delta)
 */
function parseMetricLine(line: string): Metric | null {
  const trimmed = line.trim();
  if (!trimmed) return null;
  // Forma 1: bullet ou não, **Label:** ...
  let m = trimmed.match(
    /^[-*]?\s*\*\*([^*]+?):\*\*\s*(.+?)$/,
  );
  if (!m) {
    // Forma 2: **Label**: ...
    m = trimmed.match(/^[-*]?\s*\*\*([^*]+?)\*\*[:\-—]\s*(.+?)$/);
  }
  if (!m) return null;
  const label = m[1].trim();
  let value = m[2].trim();
  let delta: string | undefined;
  // Tenta extrair delta de "(↑ 12%)" no final
  const deltaParen = value.match(/\(([^)]+)\)\s*$/);
  if (deltaParen) {
    delta = deltaParen[1].trim();
    value = value.replace(/\s*\([^)]+\)\s*$/, "").trim();
  }
  const positive = delta
    ? !/^-|↓|caiu|piorou|abaixo/i.test(delta)
    : undefined;
  return { label, value, delta, positive };
}

/**
 * Separa a seção em: prefixo de métricas (linhas iniciais que matcham
 * formato métrica, separadas por linha em branco no máximo) + resto.
 * Permite que uma seção tenha "bullets de KPI" no topo e tabela/texto
 * abaixo, renderizando ambos.
 */
function splitMetricsAndRest(body: string): { metrics: Metric[]; rest: string } {
  const lines = body.split("\n");
  const metrics: Metric[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    if (line.trim() === "") {
      // Permite linha vazia entre métricas
      i++;
      continue;
    }
    const m = parseMetricLine(line);
    if (m) {
      metrics.push(m);
      i++;
      continue;
    }
    break;
  }
  const rest = lines.slice(i).join("\n").trim();
  return { metrics, rest };
}

function NumerosCard({ section }: { section: Section }) {
  const { metrics, rest } = splitMetricsAndRest(section.body);
  return (
    <DocumentSection title="O que vi nos seus números">
      {metrics.length >= 2 && (
        <div style={{ marginBottom: rest ? 16 : 0 }}>
          <MetricGrid metrics={metrics} />
        </div>
      )}
      {(metrics.length < 2 || rest) && (
        <MarkdownLite body={metrics.length < 2 ? section.body : rest} />
      )}
    </DocumentSection>
  );
}

function parseNumberedList(body: string): string[] {
  const out: string[] = [];
  const lines = body.split("\n");
  for (const raw of lines) {
    const line = raw.trim();
    const m = line.match(/^\d+\.\s+(.+)$/);
    if (m) out.push(m[1]);
  }
  return out;
}

function RecomendacoesCard({ section }: { section: Section }) {
  const items = parseNumberedList(section.body);
  if (items.length === 0) {
    return (
      <DocumentSection title="Recomendações estratégicas">
        <MarkdownLite body={section.body} />
      </DocumentSection>
    );
  }
  return (
    <DocumentSection title={`${items.length} recomendações estratégicas`}>
      <NumberedList
        items={items}
        renderItem={(item) => (
          <MdInline text={item} />
        )}
      />
    </DocumentSection>
  );
}

function PlanoCard({ section }: { section: Section }) {
  return (
    <DocumentSection title="Plano de ação">
      <MarkdownLite body={section.body} />
    </DocumentSection>
  );
}

function GenericSectionCard({ section }: { section: Section }) {
  return (
    <DocumentSection title={section.title}>
      <MarkdownLite body={section.body} />
    </DocumentSection>
  );
}

// =============================================================================
// Document view — V1 protótipo se sections forem reconhecidas
// =============================================================================

const KNOWN_SLUGS_RESUMO = ["resumo", "resumo-em-30-segundos", "resumo-executivo"];
const KNOWN_SLUGS_NUMEROS = ["numeros", "metricas", "o-que-vi-nos-numeros", "o-que-vi-nos-seus-numeros"];
const KNOWN_SLUGS_RECOMENDACOES = ["recomendacoes", "recomendacoes-estrategicas"];
const KNOWN_SLUGS_PLANO = ["plano-de-acao", "plano", "proximos-passos"];

function DocumentView({ document, issue }: { document: IssueDocument | null; issue: Issue }) {
  const body = document?.body ?? document?.latestBody ?? null;
  const sections = useMemo(() => (body ? parseSections(body) : []), [body]);

  const renderSection = (s: Section) => {
    if (KNOWN_SLUGS_RESUMO.includes(s.slug)) return <ResumoCard key={s.slug} section={s} />;
    if (KNOWN_SLUGS_NUMEROS.includes(s.slug)) return <NumerosCard key={s.slug} section={s} />;
    if (KNOWN_SLUGS_RECOMENDACOES.includes(s.slug)) return <RecomendacoesCard key={s.slug} section={s} />;
    if (KNOWN_SLUGS_PLANO.includes(s.slug)) return <PlanoCard key={s.slug} section={s} />;
    return <GenericSectionCard key={s.slug} section={s} />;
  };

  return (
    <div style={{ padding: "12px 24px 48px", flex: 1, overflow: "auto" }}>
      <div style={{ maxWidth: 880, margin: "0 auto" }}>
        {document && (
          <div
            style={{
              display: "flex",
              gap: 8,
              marginBottom: 18,
              alignItems: "center",
              flexWrap: "wrap",
              fontSize: 11,
              color: "var(--wf-ink-3)",
            }}
          >
            <span>v{document.latestRevisionNumber}</span>
            <span>·</span>
            <span>entregue em {formatBR(issue.completedAt ?? issue.updatedAt)}</span>
          </div>
        )}

        {!body ? (
          <div className="wf-hand-note" style={{ fontSize: 14, color: "var(--wf-ink-3)" }}>
            agente ainda não entregou o documento desta tarefa
          </div>
        ) : sections.length > 0 ? (
          // Render estruturado V1
          <>
            {sections.map(renderSection)}
            <div style={{ marginTop: 24, display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => downloadAsMarkdown(issue.title, body)}
                className="wf-btn wf-btn--ghost"
                style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
                title="Baixa o entregável como arquivo .md"
              >
                <Icon name="download" size={13} /> Baixar markdown
              </button>
            </div>
          </>
        ) : (
          // Fallback: nenhuma seção H2 encontrada → markdown puro
          <MarkdownLite body={body} />
        )}

        {body && <FeedbackWidget issueId={issue.id} />}
      </div>
    </div>
  );
}

// =============================================================================
// Gallery view — V3 protótipo
// =============================================================================

type Slide = {
  index: number;
  title: string;
  visible?: string; // texto visível no slide
  visual?: string; // sugestão visual
  sub?: string; // subtexto
  raw: string;
};

function parseSlides(body: string): { slides: Slide[]; legenda: string | null } {
  // Cada slide separado por --- e iniciado com "## Slide N — Título"
  // Suporta também "## Legenda" no final
  const sections = parseSections(body);
  const slides: Slide[] = [];
  let legenda: string | null = null;
  let slideIndex = 0;

  for (const s of sections) {
    if (s.slug === "legenda" || s.slug.startsWith("legenda-")) {
      legenda = s.body;
      continue;
    }
    if (s.slug.startsWith("slide-")) {
      slideIndex += 1;
      const titleMatch = s.title.match(/Slide\s+\d+\s*[—\-:]\s*(.+)$/i);
      const title = titleMatch ? titleMatch[1].trim() : s.title;

      const visibleMatch = s.body.match(/\*\*Título visível:\*\*\s*(.+)/i);
      const visualMatch = s.body.match(/\*\*Sugestão visual:\*\*\s*(.+)/i);
      const subMatch = s.body.match(/\*\*Subtexto:\*\*\s*(.+)/i);

      slides.push({
        index: slideIndex,
        title,
        visible: visibleMatch?.[1].trim(),
        visual: visualMatch?.[1].trim(),
        sub: subMatch?.[1].trim(),
        raw: s.body,
      });
    }
  }

  return { slides, legenda };
}

function GalleryView({
  documents,
  issue,
  imageAttachments = [],
}: {
  documents: IssueDocument[];
  issue: Issue;
  imageAttachments?: IssueAttachment[];
}) {
  // O fluxo atual: 1 documento "main" com body contendo todos os slides separados por ---
  const mainDoc = documents[0] ?? null;
  const body = mainDoc?.body ?? mainDoc?.latestBody ?? "";
  const { slides, legenda } = useMemo(() => parseSlides(body), [body]);
  const hasImages = imageAttachments.length > 0;

  // Lightbox: índice da imagem aberta (null = fechado).
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [zipping, setZipping] = useState(false);

  // Lápis por imagem: mapa attachmentId → instrução de ajuste pendente.
  // "Gerar nova versão" regenera só as marcadas, preserva as outras.
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [pendingEdits, setPendingEdits] = useState<Record<string, string>>({});
  const [editModalFor, setEditModalFor] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const editCount = Object.keys(pendingEdits).length;
  const editImages = useMutation({
    mutationFn: (edits: Array<{ attachmentId: string; instruction: string }>) =>
      issuesApi.editImages(issue.id, edits),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["issues", issue.id] });
      queryClient.invalidateQueries({ queryKey: ["companies", issue.companyId, "issues"] });
      setPendingEdits({});
      // Vai pra tela de Execução (loading do card em "executando"). Quando a
      // geração termina (in_review), ela mesma redireciona pro Resultado com a
      // nova versão — o usuário fica no card, não volta pro kanban.
      navigate(`/execucao/${issue.id}`);
    },
    onError: (err) => {
      alert(`Não consegui gerar a nova versão: ${(err as Error).message}`);
    },
  });
  const lightboxImages = useMemo(
    () => imageAttachments.map((a, i) => ({ src: a.contentPath, alt: a.originalFilename ?? `slide ${i + 1}`, caption: a.originalFilename ?? undefined })),
    [imageAttachments],
  );

  // Salvar arte na biblioteca do produto — vira referência das próximas artes.
  const [savedToLibrary, setSavedToLibrary] = useState<Set<string>>(new Set());
  const saveToLibrary = useMutation({
    mutationFn: (attachmentId: string) =>
      productLibraryApi.addCreative(issue.companyId, issue.productId!, {
        ref: attachmentId,
        category: "post_oferta",
      }),
    onSuccess: (_res, attachmentId) => {
      setSavedToLibrary((prev) => new Set(prev).add(attachmentId));
    },
    onError: (err) => {
      alert(`Não consegui salvar na biblioteca: ${(err as Error).message}`);
    },
  });
  const canSaveToLibrary = !!issue.productId;

  // "Estilo da marca": marca esta arte como âncora de estilo — as próximas
  // gerações de arte desta marca recebem o anexo como referência de estilo.
  const isArtTask = !!issue.templateKey?.endsWith("_arte");
  const brandStyleAnchor = useMutation({
    mutationFn: (value: boolean) => issuesApi.setBrandStyleAnchor(issue.id, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["issues", issue.id] });
      queryClient.invalidateQueries({ queryKey: ["companies", issue.companyId, "issues"] });
    },
    onError: (err) => {
      alert(`Não consegui atualizar o estilo da marca: ${(err as Error).message}`);
    },
  });

  const colors = (i: number, total: number): { bg: string; fg: string } => {
    if (i === 0) return { bg: "var(--nd-blue)", fg: "#fff" };
    if (i === total - 1) return { bg: "var(--nd-orange)", fg: "#fff" };
    return { bg: "var(--wf-paper)", fg: "var(--wf-ink)" };
  };

  return (
    <div style={{ padding: 28, flex: 1, overflow: "auto" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "flex", gap: 6, marginBottom: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span className="wf-pill wf-pill--blue" style={{ fontSize: 11 }}>
            Conteúdo
          </span>
          <span className="wf-pill wf-pill--green" style={{ fontSize: 11 }}>
            ✓ {slides.length} {slides.length === 1 ? "peça pronta" : "peças prontas"}
          </span>
        </div>

        <h1 className="wf-display" style={{ fontSize: 36, marginBottom: 6, lineHeight: 1.05, color: "var(--nd-orange-pun)" }}>
          {mainDoc?.title ?? issue.title}
        </h1>
        <div className="wf-hand-note" style={{ fontSize: 13, marginBottom: 22 }}>
          entregue em {formatBR(issue.completedAt ?? issue.updatedAt)}
        </div>

        {hasImages && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 14,
              marginBottom: 22,
            }}
          >
            {imageAttachments.map((att, i) => {
              const saved = savedToLibrary.has(att.id);
              return (
                <div
                  key={att.id}
                  className="wf-card"
                  style={{ padding: 0, overflow: "hidden", position: "relative" }}
                >
                  {/* Lápis — ajustar SÓ esta imagem. Só em arte editável
                      (arte-*): some em slides de carrossel (carrossel-slide-*,
                      continuidade = Fase 4) e em imagens de referência. */}
                  {(att.originalFilename ?? "").startsWith("arte-") && (
                  <button
                    type="button"
                    title="Ajustar esta imagem"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditModalFor(att.id);
                      setEditDraft(pendingEdits[att.id] ?? "");
                    }}
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 2,
                      width: 30,
                      height: 30,
                      borderRadius: 8,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      border: "none",
                      background: pendingEdits[att.id]
                        ? "var(--nd-orange)"
                        : "rgba(13,21,38,0.62)",
                      color: "#fff",
                      backdropFilter: "blur(4px)",
                    }}
                  >
                    <Icon name="edit" size={14} color="#fff" />
                  </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setLightboxIdx(i)}
                    style={{ display: "block", textDecoration: "none", border: "none", padding: 0, background: "none", width: "100%", cursor: "zoom-in" }}
                  >
                    <img
                      src={att.contentPath}
                      alt={att.originalFilename ?? "arte gerada"}
                      style={{
                        display: "block",
                        width: "100%",
                        aspectRatio: "1",
                        objectFit: "cover",
                        background: "var(--wf-paper)",
                      }}
                    />
                  </button>
                  <div
                    style={{
                      padding: "8px 12px",
                      borderTop: "1px solid var(--wf-ink-5)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        color: "var(--wf-ink-3)",
                        fontFamily: "var(--wf-font-body)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {att.originalFilename ?? "arte"}
                    </span>
                    {canSaveToLibrary && (
                      <button
                        type="button"
                        onClick={() => saveToLibrary.mutate(att.id)}
                        disabled={saved || saveToLibrary.isPending}
                        className="wf-btn wf-btn--ghost wf-btn--sm"
                        style={{
                          fontSize: 10.5,
                          padding: "2px 8px",
                          whiteSpace: "nowrap",
                          color: saved ? "#1a8754" : undefined,
                        }}
                        title="Adiciona essa arte à biblioteca do produto — vira referência das próximas"
                      >
                        {saved ? "✓ na biblioteca" : "★ Salvar na biblioteca"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Barra de ajustes pendentes (lápis) — gera nova versão regenerando só
            as marcadas. */}
        {editCount > 0 && (
          <div
            className="wf-card"
            style={{
              padding: 14,
              marginBottom: 22,
              border: "1px solid var(--nd-orange)",
              background: "rgba(254,112,0,0.06)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div style={{ fontSize: 13, color: "var(--wf-ink-2)", lineHeight: 1.45 }}>
              <strong>{editCount}</strong>{" "}
              {editCount === 1 ? "imagem marcada" : "imagens marcadas"} pra ajuste. As
              demais ficam intactas na nova versão.
            </div>
            <button
              type="button"
              className="wf-btn wf-btn--primary"
              disabled={editImages.isPending}
              onClick={() =>
                editImages.mutate(
                  Object.entries(pendingEdits).map(([attachmentId, instruction]) => ({
                    attachmentId,
                    instruction,
                  })),
                )
              }
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <Icon name="sparkles" size={13} color="#fff" />
              {editImages.isPending
                ? "gerando nova versão…"
                : `Gerar nova versão (${editCount})`}
            </button>
          </div>
        )}

        {/* Modal grande do lápis — escreve o que mudar nesta imagem. */}
        {editModalFor && (
          <div
            onClick={() => setEditModalFor(null)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(5,8,16,0.55)",
              zIndex: 300,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 20,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="wf-card"
              style={{ width: "min(560px, 96vw)", padding: 20 }}
            >
              <div className="wf-display" style={{ fontSize: 18, marginBottom: 6 }}>
                Ajustar esta imagem
              </div>
              <p style={{ fontSize: 13, color: "var(--wf-ink-3)", lineHeight: 1.5, marginTop: 0 }}>
                Descreva o que mudar SÓ nesta imagem. As outras da galeria ficam
                intactas. Ao gerar a nova versão, a IA refaz só o que você pediu aqui,
                preservando o resto.
              </p>
              <textarea
                value={editDraft}
                onChange={(e) => setEditDraft(e.target.value)}
                autoFocus
                rows={4}
                placeholder="ex: troque a headline para 'Vagas abertas', deixe o resto igual"
                style={{
                  width: "100%",
                  padding: 10,
                  border: "1px solid var(--wf-ink-5)",
                  borderRadius: 8,
                  fontFamily: "var(--wf-font-body)",
                  fontSize: 13,
                  resize: "vertical",
                  background: "var(--wf-paper)",
                  lineHeight: 1.5,
                }}
              />
              <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
                {pendingEdits[editModalFor] && (
                  <button
                    type="button"
                    className="wf-btn wf-btn--ghost"
                    onClick={() => {
                      setPendingEdits((prev) => {
                        const next = { ...prev };
                        delete next[editModalFor];
                        return next;
                      });
                      setEditModalFor(null);
                    }}
                  >
                    Remover ajuste
                  </button>
                )}
                <button
                  type="button"
                  className="wf-btn wf-btn--primary"
                  disabled={!editDraft.trim()}
                  onClick={() => {
                    setPendingEdits((prev) => ({ ...prev, [editModalFor]: editDraft.trim() }));
                    setEditModalFor(null);
                  }}
                >
                  Salvar ajuste
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Baixar .zip — sempre que houver imagens, independente de doc ter slides parseados. */}
        {hasImages && (
          <div
            className="wf-card"
            style={{ padding: 14, background: "#eaf0f6", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 22 }}
          >
            <div className="wf-hand" style={{ fontSize: 14, display: "flex", alignItems: "center", gap: 5 }}>
              <Icon name="download" size={13} /> {imageAttachments.length === 1 ? "Baixar a imagem" : "Baixar as imagens"}
            </div>
            <button
              type="button"
              className="wf-btn wf-btn--primary"
              disabled={zipping}
              onClick={async () => {
                if (zipping) return;
                setZipping(true);
                try {
                  const safe = (issue.title || "imagem").replace(/[^a-z0-9-_]+/gi, "_").slice(0, 60);
                  if (imageAttachments.length === 1) {
                    // 1 imagem só: baixa direto no formato dela (.png/.jpg), sem zip.
                    const att = imageAttachments[0]!;
                    const res = await fetch(att.contentPath);
                    const blob = await res.blob();
                    const ct = att.contentType || blob.type || "";
                    const ext = ct.includes("png")
                      ? "png"
                      : ct.includes("jpeg") || ct.includes("jpg")
                        ? "jpg"
                        : ct.includes("webp")
                          ? "webp"
                          : att.originalFilename?.split(".").pop() || "png";
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `${safe}.${ext}`;
                    document.body.appendChild(a); a.click(); a.remove();
                    URL.revokeObjectURL(url);
                  } else {
                    // 2+ imagens: empacota em .zip.
                    const JSZip = (await import("jszip")).default;
                    const zip = new JSZip();
                    for (let i = 0; i < imageAttachments.length; i++) {
                      const att = imageAttachments[i]!;
                      const res = await fetch(att.contentPath);
                      const blob = await res.blob();
                      const name = att.originalFilename || `imagem-${String(i + 1).padStart(2, "0")}.png`;
                      zip.file(name, blob);
                    }
                    const out = await zip.generateAsync({ type: "blob" });
                    const url = URL.createObjectURL(out);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `${safe}.zip`;
                    document.body.appendChild(a); a.click(); a.remove();
                    URL.revokeObjectURL(url);
                  }
                } catch (err) {
                  console.error("download falhou:", err);
                } finally {
                  setZipping(false);
                }
              }}
              style={{ fontSize: 13, padding: "8px 14px", display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <Icon name="download" size={13} />{" "}
              {zipping
                ? imageAttachments.length === 1
                  ? "Baixando…"
                  : "Empacotando…"
                : imageAttachments.length === 1
                  ? "Baixar imagem"
                  : `Baixar .zip (${imageAttachments.length})`}
            </button>
          </div>
        )}

        {/* Estilo da marca — marca esta arte como âncora de estilo das próximas
            gerações desta marca (resolve o "aprovamos o que deu certo e não
            replica"). Só em tarefas de arte com imagem. */}
        {hasImages && isArtTask && (
          <div
            className="wf-card"
            style={{ padding: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 22 }}
          >
            <div style={{ fontSize: 13, display: "flex", alignItems: "flex-start", gap: 6, maxWidth: 470, lineHeight: 1.4 }}>
              <Icon name="star" size={14} />
              <span>
                <strong>Estilo da marca.</strong> Marque se esta é a arte que define o visual da marca — as próximas gerações vão seguir o estilo dela (paleta, tipografia, composição).
              </span>
            </div>
            <button
              type="button"
              className={issue.isBrandStyleAnchor ? "wf-btn wf-btn--primary" : "wf-btn"}
              disabled={brandStyleAnchor.isPending}
              onClick={() => brandStyleAnchor.mutate(!issue.isBrandStyleAnchor)}
              style={{ fontSize: 13, padding: "8px 14px", display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}
            >
              <Icon name={issue.isBrandStyleAnchor ? "check" : "star"} size={13} />{" "}
              {brandStyleAnchor.isPending
                ? "Salvando…"
                : issue.isBrandStyleAnchor
                  ? "É o estilo da marca"
                  : "Marcar como estilo da marca"}
            </button>
          </div>
        )}

        {slides.length === 0 ? (
          !hasImages && (
            <div className="wf-hand-note" style={{ fontSize: 13, color: "var(--wf-ink-3)" }}>
              agente ainda não entregou os slides
            </div>
          )
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                gap: 12,
                marginBottom: 18,
              }}
            >
              {slides.map((slide, i) => {
                const { bg, fg } = colors(i, slides.length);
                return (
                  <div
                    key={slide.index}
                    className="wf-card"
                    style={{
                      padding: 0,
                      overflow: "hidden",
                      background: bg,
                      color: fg,
                    }}
                  >
                    <div style={{ aspectRatio: "1", display: "flex", flexDirection: "column", padding: 12 }}>
                      <div
                        className="wf-hand-note"
                        style={{
                          fontSize: 10,
                          color: fg === "#fff" ? "rgba(255,255,255,0.75)" : "var(--wf-ink-3)",
                          marginBottom: 4,
                        }}
                      >
                        Slide {slide.index} de {slides.length}
                      </div>
                      <div className="wf-display" style={{ fontSize: 14, lineHeight: 1.2, marginBottom: 6 }}>
                        {slide.title}
                      </div>
                      {slide.visible && (
                        <div style={{ fontSize: 12, lineHeight: 1.4, marginBottom: 8, opacity: 0.9 }}>
                          {slide.visible.length > 120 ? slide.visible.slice(0, 120) + "…" : slide.visible}
                        </div>
                      )}
                      {slide.visual && (
                        <div
                          style={{
                            fontSize: 10,
                            opacity: fg === "#fff" ? 0.7 : 0.6,
                            marginTop: "auto",
                            paddingTop: 6,
                            borderTop: `1px dashed ${fg === "#fff" ? "rgba(255,255,255,0.3)" : "var(--wf-ink-4)"}`,
                            fontStyle: "italic",
                          }}
                        >
                          🎨 {slide.visual.length > 80 ? slide.visual.slice(0, 80) + "…" : slide.visual}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {legenda && (
                <div
                  className="wf-card wf-card--dashed"
                  style={{ padding: 0, overflow: "hidden" }}
                >
                  <div style={{ aspectRatio: "1", display: "flex", flexDirection: "column", padding: 12 }}>
                    <div className="wf-hand-note" style={{ fontSize: 10, color: "var(--wf-ink-3)", marginBottom: 4 }}>
                      caption
                    </div>
                    <div className="wf-display" style={{ fontSize: 14, marginBottom: 6 }}>
                      Legenda
                    </div>
                    <div style={{ fontSize: 11, lineHeight: 1.4, color: "var(--wf-ink-2)", flex: 1, overflow: "hidden" }}>
                      {legenda.length > 200 ? legenda.slice(0, 200) + "…" : legenda}
                    </div>
                  </div>
                </div>
              )}
            </div>

          </>
        )}
      </div>
      <ImageLightbox
        images={lightboxImages}
        index={lightboxIdx}
        onClose={() => setLightboxIdx(null)}
        onChange={setLightboxIdx}
      />
    </div>
  );
}

// =============================================================================
// ReopenTaskButton — reabre um card concluído pra gerar uma NOVA versão
// =============================================================================

function ReopenTaskButton({ issue }: { issue: Issue }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Briefing existente persistido no executionState (quando o card foi criado
  // com briefing de arte). Pré-preenche o formulário pro usuário só ajustar.
  const existingBriefing = useMemo<ArtBriefing>(() => {
    const raw = (issue.executionState as { artBriefing?: ArtBriefing } | null | undefined)
      ?.artBriefing;
    return raw && typeof raw === "object" ? { ...raw } : {};
  }, [issue.executionState]);

  const [briefing, setBriefing] = useState<ArtBriefing>(existingBriefing);

  // Descobre se o template gera arte (pra mostrar o briefing). Pros que não
  // geram arte, reabrir só re-roda a geração sem perguntas.
  const templatesQuery = useQuery({
    queryKey: ["templates"],
    queryFn: () => templatesApi.list(),
    staleTime: 10 * 60_000,
  });
  const template = useMemo(
    () =>
      issue.templateKey
        ? templatesQuery.data?.find((t) => t.key === issue.templateKey)
        : null,
    [issue.templateKey, templatesQuery.data],
  );
  const producesArt =
    !!template &&
    (template.key.endsWith("_arte") ||
      !!template.nextTemplateKey?.endsWith("_arte"));

  const reopen = useMutation({
    mutationFn: () =>
      issuesApi.reopen(
        issue.id,
        producesArt &&
          Object.values(briefing).some((v) => (v ?? "").trim())
          ? briefing
          : null,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["issues", issue.id] });
      queryClient.invalidateQueries({
        queryKey: ["companies", issue.companyId, "issues"],
      });
      queryClient.invalidateQueries({
        queryKey: ["companies", issue.companyId, "dashboard"],
      });
      setOpen(false);
      // Continua no mesmo card — a nova versão é gerada aqui mesmo.
      navigate(`/execucao/${issue.id}`);
    },
  });

  const onOpen = () => {
    setBriefing(existingBriefing);
    setOpen(true);
  };

  return (
    <>
      <div
        style={{
          padding: "12px 14px",
          borderBottom: "1px solid var(--wf-ink-5)",
        }}
      >
        <button
          type="button"
          className="wf-btn wf-btn--ghost"
          onClick={onOpen}
          style={{ width: "100%", justifyContent: "center", gap: 6 }}
        >
          <Icon name="refresh" size={14} />
          Reabrir tarefa
        </button>
        <div
          style={{
            fontSize: 11,
            color: "var(--wf-ink-3)",
            marginTop: 6,
            lineHeight: 1.4,
          }}
        >
          Gera uma nova versão em cima desta tarefa. As versões anteriores ficam
          guardadas no histórico.
        </div>
      </div>

      {open && (
        <div
          onClick={() => !reopen.isPending && setOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 320,
            background: "rgba(5,8,16,0.55)",
            backdropFilter: "blur(2px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="wf-card"
            style={{
              width: "100%",
              maxWidth: 520,
              maxHeight: "85vh",
              overflow: "auto",
              padding: 0,
              background: "var(--wf-paper)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                padding: "16px 18px",
                borderBottom: "1px solid var(--wf-ink-5)",
              }}
            >
              <div className="wf-display" style={{ fontSize: 20, lineHeight: 1.15 }}>
                Reabrir tarefa
              </div>
              <div
                className="wf-hand-note"
                style={{ fontSize: 12.5, marginTop: 4, color: "var(--wf-ink-2)" }}
              >
                {producesArt
                  ? "Ajuste o briefing e gere uma nova versão da arte. O histórico desta tarefa fica preservado."
                  : "A tarefa volta pra fila e gera uma nova versão. O histórico fica preservado."}
              </div>
            </div>

            <div
              style={{
                padding: 18,
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              {producesArt && (
                <ArtBriefingQuestions
                  value={briefing}
                  onChange={setBriefing}
                  templateKey={issue.templateKey ?? undefined}
                />
              )}

              {reopen.isError && (
                <div style={{ fontSize: 12, color: "#D86B5A" }}>
                  Não foi possível reabrir a tarefa. Tente novamente.
                </div>
              )}
            </div>

            <div
              style={{
                padding: "14px 18px",
                borderTop: "1px solid var(--wf-ink-5)",
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
              }}
            >
              <button
                type="button"
                className="wf-btn wf-btn--ghost"
                onClick={() => setOpen(false)}
                disabled={reopen.isPending}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="wf-btn wf-btn--primary"
                onClick={() => reopen.mutate()}
                disabled={reopen.isPending}
              >
                {reopen.isPending ? "Reabrindo..." : "Reabrir e gerar nova versão"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// =============================================================================
// TaskInfoPanel — sidebar com informações da tarefa
// =============================================================================

function TaskInfoPanel({
  issue,
  onClose,
  fullWidth,
  closeable = true,
}: {
  issue: Issue;
  onClose: () => void;
  /** No mobile o painel vira um drawer overlay — ocupa 100% do wrapper. */
  fullWidth?: boolean;
  /** No desktop o painel fica SEMPRE aberto (sem X), pra o user não se perder
   *  ao fechar e não achar como reabrir. Só o drawer mobile é fechável. */
  closeable?: boolean;
}) {
  // Pai (se eu sou sub-task)
  const parentQuery = useQuery({
    queryKey: ["issues", issue.parentId, "detail"],
    queryFn: () => issuesApi.detail(issue.parentId!),
    enabled: !!issue.parentId,
  });

  // Filhos (se eu tenho sub-tasks)
  const childrenQuery = useQuery({
    queryKey: ["issues", "children-of", issue.id],
    queryFn: () => issuesApi.list(issue.companyId, { parentId: issue.id }),
    enabled: !!issue.id,
  });

  // Histórico de versões + aprovações/reprovações.
  // Combina document revisions (cada vez que agente entrega) + comments
  // que contêm [REPROVADO PELO USUÁRIO] (decisões do user). Ordenado
  // cronologicamente pra mostrar a história da tarefa em ordem.
  const revisionsQuery = useQuery({
    queryKey: ["issues", issue.id, "document-revisions"],
    queryFn: () => issuesApi.documentRevisions(issue.id),
    enabled: !!issue.id,
    // Histórico de versões também sempre fresco ao montar (mesma razão do doc).
    refetchOnMount: "always",
  });
  const commentsQuery = useQuery({
    queryKey: ["issues", issue.id, "comments"],
    queryFn: () => issuesApi.comments(issue.id),
    enabled: !!issue.id,
  });
  // Anexos pra montar as versões de IMAGEM no histórico (uma entrada por
  // version das artes geradas). Mesma queryKey usada na página principal —
  // reaproveita o cache, sem fetch extra.
  const attachmentsQuery = useQuery({
    queryKey: ["issues", issue.id, "attachments"],
    queryFn: () => issuesApi.attachments(issue.id),
    enabled: !!issue.id,
  });

  // Versão aberta no modal (texto OU imagem). null = fechado.
  const [openVersion, setOpenVersion] = useState<
    | { type: "text"; revisionNumber: number; ts: string; body: string }
    | { type: "image"; version: number; ts: string; images: IssueAttachment[] }
    | null
  >(null);

  const timeline = useMemo(() => {
    type Item = {
      kind: "created" | "delivered" | "delivered-image" | "rejected" | "approved";
      ts: string;
      label: string;
      detail?: string;
      /** Payload pra abrir no modal ao clicar (só itens de versão têm). */
      open?:
        | { type: "text"; revisionNumber: number; ts: string; body: string }
        | { type: "image"; version: number; ts: string; images: IssueAttachment[] };
    };
    const items: Item[] = [
      { kind: "created", ts: issue.createdAt, label: "Tarefa criada" },
    ];
    for (const rev of revisionsQuery.data ?? []) {
      items.push({
        kind: "delivered",
        ts: rev.createdAt,
        label: `Versão ${rev.revisionNumber} entregue`,
        detail: rev.changeSummary ?? undefined,
        open: {
          type: "text",
          revisionNumber: rev.revisionNumber,
          ts: rev.createdAt,
          body: rev.body,
        },
      });
    }
    // Versões de IMAGEM — agrupa artes geradas por `version`. Uma entrada por
    // version, datada pelo createdAt mínimo do lote.
    const arts = (attachmentsQuery.data ?? []).filter(
      (a) =>
        (a.contentType ?? "").startsWith("image/") &&
        ((a.originalFilename ?? "").startsWith("arte-") ||
          (a.originalFilename ?? "").startsWith("carrossel-slide-")),
    );
    const byVersion = new Map<number, IssueAttachment[]>();
    for (const a of arts) {
      const v = a.version ?? 1;
      const list = byVersion.get(v) ?? [];
      list.push(a);
      byVersion.set(v, list);
    }
    for (const [version, list] of byVersion) {
      const minTs = list
        .map((a) => a.createdAt)
        .sort((x, y) => x.localeCompare(y))[0];
      const sorted = [...list].sort((a, b) =>
        (a.originalFilename ?? "").localeCompare(b.originalFilename ?? ""),
      );
      items.push({
        kind: "delivered-image",
        ts: minTs,
        label: `Versão ${version} entregue (imagem)`,
        detail:
          sorted.length > 1 ? `${sorted.length} imagens` : undefined,
        open: { type: "image", version, ts: minTs, images: sorted },
      });
    }
    for (const c of commentsQuery.data ?? []) {
      const body = c.body ?? "";
      if (body.startsWith("[REPROVADO PELO USUÁRIO]")) {
        items.push({
          kind: "rejected",
          ts: c.createdAt,
          label: "Reprovado",
          detail: body.replace("[REPROVADO PELO USUÁRIO]", "").trim().slice(0, 140),
        });
      }
    }
    if (issue.status === "done") {
      items.push({
        kind: "approved",
        ts: issue.updatedAt,
        label: "Aprovado pelo usuário",
      });
    }
    return items.sort((a, b) => a.ts.localeCompare(b.ts));
  }, [
    issue.createdAt,
    issue.updatedAt,
    issue.status,
    revisionsQuery.data,
    commentsQuery.data,
    attachmentsQuery.data,
  ]);

  const children = childrenQuery.data ?? [];
  const hasRelated = !!parentQuery.data || children.length > 0;

  return (
    <aside
      style={{
        width: fullWidth ? "100%" : 288,
        borderLeft: fullWidth ? "none" : "1px solid var(--wf-ink-5)",
        background: "var(--wf-paper)",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        overflow: "auto",
      }}
    >
      <div
        style={{
          padding: "12px 14px",
          borderBottom: "1px solid var(--wf-ink-5)",
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
        }}
      >
        <div style={{ flex: 1 }}>
          <div className="wf-hand-note" style={{ fontSize: 11 }}>SOBRE ESTA TAREFA</div>
          <div className="wf-display" style={{ fontSize: 16, lineHeight: 1.1 }}>
            {issue.title}
          </div>
        </div>
        {closeable && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}
          >
            <Icon name="x" size={14} />
          </button>
        )}
      </div>

      {/* Ações de revisão — só aparecem quando a tarefa terminou e está
          esperando o usuário (in_review). */}
      {issue.status === "in_review" && <ApprovalBar issue={issue} />}

      {/* Reabrir — só pra tarefa concluída (done). Gera uma nova versão em
          cima do mesmo card, preservando o histórico. */}
      {issue.status === "done" && <ReopenTaskButton issue={issue} />}

      <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 14, fontSize: 13 }}>
        <InfoField label="Status">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <StatusPill status={issue.status} />
            {issue.status === "done" && issue.executionState?.rejectedAndClosed && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#d92d4a",
                  background: "rgba(217,45,74,0.10)",
                  padding: "2px 8px",
                  borderRadius: 999,
                  textTransform: "uppercase",
                  letterSpacing: "0.02em",
                }}
                title={issue.executionState?.rejectedReason ?? undefined}
              >
                Reprovado
              </span>
            )}
          </span>
        </InfoField>
        {issue.identifier && (
          <InfoField label="ID">
            <span className="wf-pill wf-pill--gray" style={{ fontSize: 11 }}>
              {issue.identifier}
            </span>
          </InfoField>
        )}
        <InfoField label="Criado em">{formatBR(issue.createdAt)}</InfoField>
        <InfoField label="Atualizado">{formatBR(issue.updatedAt)}</InfoField>

        {hasRelated && (
          <div
            style={{
              marginTop: 6,
              paddingTop: 14,
              borderTop: "1px solid var(--wf-ink-5)",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <div className="wf-hand-note" style={{ fontSize: 11 }}>
              TAREFAS RELACIONADAS
            </div>

            {parentQuery.data && (
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: "var(--wf-ink-3)",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  ↑ Tarefa pai
                </div>
                <RelatedTaskRow
                  id={parentQuery.data.id}
                  title={parentQuery.data.title}
                  status={parentQuery.data.status}
                  identifier={parentQuery.data.identifier}
                />
              </div>
            )}

            {children.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: "var(--wf-ink-3)",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  ↓ Sub-tarefas ({children.length})
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {children.map((c) => (
                    <RelatedTaskRow
                      key={c.id}
                      id={c.id}
                      title={c.title}
                      status={c.status}
                      identifier={c.identifier}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Histórico — timeline cronológica de versões + aprovações/reprovações */}
        {timeline.length > 1 && (
          <div
            style={{
              marginTop: 6,
              paddingTop: 14,
              borderTop: "1px solid var(--wf-ink-5)",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <div className="wf-hand-note" style={{ fontSize: 11 }}>
              HISTÓRICO
            </div>
            <ol
              style={{
                listStyle: "none",
                margin: 0,
                padding: 0,
                display: "flex",
                flexDirection: "column",
                gap: 0,
              }}
            >
              {timeline.map((item, i) => {
                const colors = {
                  created: { bg: "rgba(254,112,0,0.10)", fg: "#FE7000", icon: "+" },
                  delivered: { bg: "rgba(74,111,168,0.10)", fg: "#4A6FA8", icon: "↑" },
                  "delivered-image": { bg: "rgba(74,111,168,0.10)", fg: "#4A6FA8", icon: "▦" },
                  rejected: { bg: "rgba(216,107,90,0.14)", fg: "#D86B5A", icon: "✕" },
                  approved: { bg: "rgba(60,140,60,0.14)", fg: "#3d7d3d", icon: "✓" },
                }[item.kind];
                const isLast = i === timeline.length - 1;
                const clickable = !!item.open;
                return (
                  <li
                    key={i}
                    style={{
                      display: "flex",
                      gap: 10,
                      paddingBottom: isLast ? 0 : 10,
                      position: "relative",
                    }}
                  >
                    {!isLast && (
                      <span
                        aria-hidden
                        style={{
                          position: "absolute",
                          left: 9.5,
                          top: 22,
                          bottom: 2,
                          width: 1,
                          background: "var(--wf-ink-5)",
                        }}
                      />
                    )}
                    <span
                      style={{
                        width: 20,
                        height: 20,
                        flexShrink: 0,
                        borderRadius: "50%",
                        background: colors.bg,
                        color: colors.fg,
                        fontSize: 11,
                        fontWeight: 800,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginTop: 1,
                      }}
                    >
                      {colors.icon}
                    </span>
                    {clickable ? (
                      <button
                        type="button"
                        onClick={() => setOpenVersion(item.open ?? null)}
                        title="Ver esta versão"
                        style={{
                          flex: 1,
                          minWidth: 0,
                          textAlign: "left",
                          background: "none",
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                          font: "inherit",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12.5,
                            fontWeight: 600,
                            color: "var(--nd-blue, #4A6FA8)",
                            textDecoration: "underline",
                            textUnderlineOffset: 2,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          {item.label}
                          <Icon name="eye" size={12} />
                        </div>
                        <div
                          style={{
                            fontSize: 10.5,
                            color: "var(--wf-ink-3)",
                            marginTop: 1,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {formatBR(item.ts)}
                        </div>
                        {item.detail && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "var(--wf-ink-2)",
                              marginTop: 4,
                              lineHeight: 1.4,
                              fontStyle: "italic",
                            }}
                          >
                            “{item.detail}”
                          </div>
                        )}
                      </button>
                    ) : (
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 12.5,
                            fontWeight: 600,
                            color: "var(--wf-ink)",
                          }}
                        >
                          {item.label}
                        </div>
                        <div
                          style={{
                            fontSize: 10.5,
                            color: "var(--wf-ink-3)",
                            marginTop: 1,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {formatBR(item.ts)}
                        </div>
                        {item.detail && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "var(--wf-ink-2)",
                              marginTop: 4,
                              lineHeight: 1.4,
                              fontStyle: "italic",
                            }}
                          >
                            “{item.detail}”
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>
        )}
      </div>

      {openVersion && (
        <VersionModal data={openVersion} onClose={() => setOpenVersion(null)} />
      )}
    </aside>
  );
}

// =============================================================================
// VersionModal — mostra o conteúdo de uma versão antiga (texto ou imagem)
// =============================================================================

function VersionModal({
  data,
  onClose,
}: {
  data:
    | { type: "text"; revisionNumber: number; ts: string; body: string }
    | { type: "image"; version: number; ts: string; images: IssueAttachment[] };
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const versionNumber = data.type === "text" ? data.revisionNumber : data.version;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 300,
        background: "rgba(5,8,16,0.55)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="wf-card"
        style={{
          width: "100%",
          maxWidth: data.type === "image" ? 880 : 720,
          maxHeight: "85vh",
          overflow: "auto",
          padding: 0,
          background: "var(--wf-paper)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid var(--wf-ink-5)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 8,
            position: "sticky",
            top: 0,
            background: "var(--wf-paper)",
            zIndex: 1,
          }}
        >
          <div>
            <div className="wf-display" style={{ fontSize: 20, lineHeight: 1.1 }}>
              Versão {versionNumber}
            </div>
            <div
              className="wf-hand-note"
              style={{ fontSize: 12, marginTop: 2 }}
            >
              entregue em {formatBR(data.ts)}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}
          >
            <Icon name="x" size={16} />
          </button>
        </div>

        <div style={{ padding: 18 }}>
          {data.type === "text" ? (
            <MarkdownLite body={data.body} />
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fill, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              {data.images.map((att) => (
                <div
                  key={att.id}
                  className="wf-card"
                  style={{ padding: 0, overflow: "hidden" }}
                >
                  <img
                    src={att.contentPath}
                    alt={att.originalFilename ?? "arte"}
                    style={{
                      display: "block",
                      width: "100%",
                      aspectRatio: "1",
                      objectFit: "cover",
                      background: "var(--wf-paper)",
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Página principal
// =============================================================================

export default function Resultado() {
  const { id: issueId } = useParams<{ id: string }>();
  const [taskOpen, setTaskOpen] = useState(false);
  const [calendarReviewOpen, setCalendarReviewOpen] = useState(false);
  const [calendarCreated, setCalendarCreated] = useState<number | null>(null);
  const companyId = useCompanyId();
  const isMobile = useIsMobile();

  const queryClient = useQueryClient();

  const issueQuery = useQuery({
    queryKey: ["issues", issueId, "detail"],
    queryFn: () => issuesApi.detail(issueId!),
    enabled: !!issueId,
    // Sempre re-busca ao montar/voltar pra página. Sem isso, o staleTime global
    // de 30s servia a versão VELHA quando o usuário reabria a tarefa logo após
    // pedir um ajuste (bug recorrente: via a v1 mesmo com a v2 já entregue).
    refetchOnMount: "always",
    // Enquanto o agente re-roda um ajuste, faz poll pra detectar a transição de
    // status (todo/in_progress → in_review) sem precisar recarregar.
    refetchInterval: (q) => {
      const s = (q.state.data as { status?: string } | undefined)?.status;
      return s === "in_progress" || s === "todo" ? 4000 : false;
    },
  });

  // Agente mexendo (re-entregando um ajuste) → poll dos documentos pra a nova
  // versão aparecer sozinha mesmo se o usuário ficar parado na tela.
  const agentWorking =
    issueQuery.data?.status === "in_progress" || issueQuery.data?.status === "todo";

  const docsQuery = useQuery({
    queryKey: ["issues", issueId, "documents"],
    queryFn: () => issuesApi.documents(issueId!),
    enabled: !!issueId,
    refetchOnMount: "always",
    refetchInterval: agentWorking ? 4000 : false,
  });

  // Quando a tarefa volta pra revisão/conclui (agente entregou a nova versão),
  // força a re-busca do documento + histórico — garante que a v2 apareça na
  // hora, sem depender do staleTime nem de o usuário recarregar.
  const issueStatus = issueQuery.data?.status;
  useEffect(() => {
    if (issueStatus === "in_review" || issueStatus === "done") {
      queryClient.invalidateQueries({ queryKey: ["issues", issueId, "documents"] });
      queryClient.invalidateQueries({ queryKey: ["issues", issueId, "document-revisions"] });
    }
  }, [issueStatus, issueId, queryClient]);

  const attachmentsQuery = useQuery({
    queryKey: ["issues", issueId, "attachments"],
    queryFn: () => issuesApi.attachments(issueId!),
    enabled: !!issueId,
  });

  // Quando a tarefa está esperando revisão, o painel lateral (que agora
  // carrega os botões de aprovar/reprovar) abre por padrão. Uma vez só —
  // se o usuário fechar, respeita. No mobile não abre sozinho (cobriria a
  // tela inteira) — o usuário toca em "Sobre a tarefa" pra abrir.
  const didDefaultOpenRef = useRef(false);
  useEffect(() => {
    if (
      !didDefaultOpenRef.current &&
      !isMobile &&
      issueQuery.data?.status === "in_review"
    ) {
      didDefaultOpenRef.current = true;
      setTaskOpen(true);
    }
  }, [issueQuery.data?.status, isMobile]);

  if (!issueId) {
    return <div style={{ padding: 28 }} className="wf-hand-note">ID da tarefa não fornecido.</div>;
  }
  if (issueQuery.isLoading || docsQuery.isLoading) {
    return <div style={{ padding: 28 }} className="wf-hand-note">carregando resultado…</div>;
  }
  if (issueQuery.isError) {
    return (
      <div style={{ padding: 28, color: "var(--nd-orange-pun)" }}>
        Erro: {(issueQuery.error as Error).message}
      </div>
    );
  }

  const issue = issueQuery.data as Issue;
  const documents = docsQuery.data ?? [];
  const attachments = attachmentsQuery.data ?? [];
  // Artes geradas pelo pipeline ("arte-*" / "carrossel-slide-*") são versionadas.
  // A galeria principal mostra SÓ a versão mais nova; as antigas ficam no
  // histórico clicável. Refs subidas pelo usuário (version 1, sem prefixo de
  // arte) não entram nessa lógica e seguem visíveis.
  const isArtAttachment = (a: IssueAttachment) =>
    (a.originalFilename ?? "").startsWith("arte-") ||
    (a.originalFilename ?? "").startsWith("carrossel-slide-");
  const maxArtVersion = attachments
    .filter(isArtAttachment)
    .reduce((max, a) => Math.max(max, a.version ?? 1), 0);
  const imageAttachments = attachments
    .filter((a) => (a.contentType ?? "").startsWith("image/"))
    // Esconde artes de versões antigas (mantém só a versão máxima). Não-artes
    // não têm versão de arte e passam sempre.
    .filter((a) => !isArtAttachment(a) || (a.version ?? 1) === maxArtVersion)
    // Ordena por nome do arquivo (ASC) — carrossel-slide-01, -02, -03 nessa
    // ordem. A API devolve por created_at DESC, o que invertia a sequência.
    .sort((a, b) => (a.originalFilename ?? "").localeCompare(b.originalFilename ?? ""));
  const mainDoc = documents.find((d) => d.key === "main") ?? documents[0] ?? null;
  const isGallery =
    mainDoc?.format === "gallery" ||
    documents.some((d) => d.format === "gallery") ||
    // Sub-tasks de arte podem não ter document — se há imagens anexadas
    // pelo agente, mostra galeria delas.
    (issue.templateKey?.endsWith("_arte") && imageAttachments.length > 0);

  const templateMeta = getTemplateMeta(issue.templateKey);
  const TemplateIcon = templateMeta?.icon ?? Sparkles;

  return (
    <div style={{ height: "100%", display: "flex", background: "var(--wf-paper)" }}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <PageHeader
          backTo="/quadro"
          backLabel="Quadro"
          icon={TemplateIcon}
          kicker={templateMeta?.label ?? "entregável"}
          title={issue.title}
          maxWidth={880}
          rightSlot={
            <>
              <StatusPill status={issue.status} />
              {/* Toggle só no mobile — no desktop o painel fica sempre aberto. */}
              {isMobile && (
                <button
                  type="button"
                  onClick={() => setTaskOpen((v) => !v)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    fontSize: 12,
                    padding: "5px 12px",
                    borderRadius: 999,
                    background: taskOpen ? "rgba(255,112,0,0.08)" : "var(--wf-paper)",
                    color: taskOpen ? "var(--nd-orange)" : "var(--wf-ink-2)",
                    border: `1px solid ${taskOpen ? "var(--nd-orange)" : "var(--wf-ink-5)"}`,
                    cursor: "pointer",
                    fontFamily: "var(--wf-font-body)",
                    fontWeight: 500,
                  }}
                >
                  <Icon name={taskOpen ? "arrow_r" : "arrow_l"} size={12} />
                  Sobre a tarefa
                </button>
              )}
            </>
          }
        />

        {issue.status === "blocked" && (
          <div
            style={{
              padding: "20px 24px 0",
              maxWidth: 880,
              margin: "0 auto",
              width: "100%",
            }}
          >
            <BlockedBanner issue={issue} />
          </div>
        )}
        {/* Calendário editorial: CTA pra criar as tarefas dos posts de uma vez.
            Só quando o calendário FOI entregue (tem documento + status de
            revisão/concluído) — nada de CTA em calendário bloqueado/vazio. */}
        {issue.templateKey === "calendario_editorial" &&
          mainDoc &&
          (issue.status === "in_review" || issue.status === "done") && (
          <div style={{ padding: "16px 24px 0", maxWidth: 880, margin: "0 auto", width: "100%" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: "14px 18px",
                borderRadius: 12,
                border: "1px solid var(--nd-orange)",
                background: "rgba(255,112,0,0.06)",
              }}
            >
              <CalendarClock size={20} strokeWidth={1.75} color="var(--nd-orange)" style={{ flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--wf-ink)" }}>
                  {calendarCreated != null
                    ? `${calendarCreated} ${calendarCreated === 1 ? "tarefa criada" : "tarefas criadas"} no Quadro.`
                    : "Transforme este calendário em tarefas"}
                </div>
                <div style={{ fontSize: 12.5, color: "var(--wf-ink-2)" }}>
                  {calendarCreated != null
                    ? "Elas estão datadas e prontas pra executar no dia certo."
                    : "Cria uma tarefa datada por post, herdando as bases que você escolheu aqui."}
                </div>
              </div>
              <button
                type="button"
                className="wf-btn wf-btn--primary"
                onClick={() => setCalendarReviewOpen(true)}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}
              >
                <CalendarClock size={14} strokeWidth={2} />
                {calendarCreated != null ? "Revisar de novo" : "Criar tarefas"}
              </button>
            </div>
          </div>
        )}
        {isGallery ? (
          <GalleryView
            documents={documents}
            issue={issue}
            imageAttachments={imageAttachments}
          />
        ) : (
          <DocumentView document={mainDoc} issue={issue} />
        )}
      </div>
      {calendarReviewOpen && companyId && (
        <CalendarTaskReviewModal
          companyId={companyId}
          issueId={issue.id}
          onClose={(created) => {
            setCalendarReviewOpen(false);
            if (typeof created === "number") setCalendarCreated(created);
          }}
        />
      )}
      {(isMobile ? taskOpen : true) &&
        (isMobile ? (
          <>
            <div
              onClick={() => setTaskOpen(false)}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(5,8,16,0.45)",
                zIndex: 199,
              }}
            />
            <div
              style={{
                position: "fixed",
                top: 0,
                right: 0,
                bottom: 0,
                width: "min(360px, 92vw)",
                zIndex: 200,
                display: "flex",
                boxShadow: "-4px 0 24px rgba(5,8,16,0.18)",
              }}
            >
              <TaskInfoPanel
                issue={issue}
                onClose={() => setTaskOpen(false)}
                fullWidth
              />
            </div>
          </>
        ) : (
          <TaskInfoPanel
            issue={issue}
            onClose={() => setTaskOpen(false)}
            closeable={false}
          />
        ))}
    </div>
  );
}
